import { Link, useSearchParams } from "react-router-dom";
import SiteHeader from "@/components/feature/SiteHeader";
import SiteFooter from "@/components/feature/SiteFooter";
import FilmCard from "@/components/feature/FilmCard";
import { films } from "@/mocks/films";
import { specials } from "@/mocks/specials";
import useDocumentMeta from "@/hooks/useDocumentMeta";

export default function Special() {
  const [searchParams, setSearchParams] = useSearchParams();
  const activeId = searchParams.get("id") ?? specials[0]?.id ?? "";
  const active = specials.find((item) => item.id === activeId) ?? specials[0];

  useDocumentMeta({
    title: active ? `${active.title} - 韩国AV专题合集` : "专题合集 - 韩国AV",
    description: active
      ? `${active.title}：${active.intro.slice(0, 80)}… 含专题片单、推荐理由与相关专题导航。`
      : "韩国AV专题合集页，按主题整理韩国影视作品片单与推荐理由。",
    keywords: active ? `${active.title},韩国AV专题,影视片单,${active.subtitle}` : "韩国AV专题,影视合集",
  });

  if (!active) {
    return (
      <div className="flex min-h-screen w-full flex-col bg-background-50">
        <SiteHeader />
        <main className="mx-auto flex w-full max-w-[900px] flex-1 flex-col items-center justify-center px-4 py-24 text-center">
          <h1 className="font-heading text-xl font-semibold text-foreground-950">暂无相关专题</h1>
          <p className="mt-2 text-[13px] text-foreground-500">专题内容正在整理中，可以先浏览影视分类。</p>
          <Link
            to="/category"
            className="mt-5 inline-flex whitespace-nowrap rounded-md bg-primary-500 px-4 py-2.5 text-[13px] font-medium text-background-50"
          >
            浏览影视分类
          </Link>
        </main>
        <SiteFooter />
      </div>
    );
  }

  const activeFilms = active.filmIds
    .map((filmId) => films.find((film) => film.id === filmId))
    .filter((film) => film !== undefined);

  const relatedSpecials = specials.filter((item) => item.id !== active.id);

  return (
    <div className="flex min-h-screen w-full flex-col bg-background-50">
      <SiteHeader />

      <main className="mx-auto w-full max-w-[1280px] flex-1 px-4 pb-4 pt-6 md:px-6 lg:px-8">
        <nav aria-label="面包屑导航" className="flex items-center gap-1.5 text-[12px] text-foreground-500">
          <Link to="/" className="transition-colors hover:text-primary-700">
            首页
          </Link>
          <i className="ri-arrow-right-s-line text-foreground-300" />
          <span className="text-foreground-800">专题合集</span>
        </nav>

        <div className="mt-4 flex flex-col gap-2 md:flex-row md:items-end md:justify-between">
          <div>
            <span className="text-[11px] font-medium uppercase tracking-[0.18em] text-accent-700">Curated Collection</span>
            <h1 className="mt-1.5 font-heading text-[26px] font-semibold leading-tight text-foreground-950 md:text-[30px]">
              专题合集 · 按主题看片单
            </h1>
            <p className="mt-2 max-w-2xl text-[13px] leading-6 text-foreground-600">
              我们按气质、题材与观看场景整理作品，每个专题都是一个可以直接照着看完的小片单。
            </p>
          </div>
          <p className="text-[11px] text-foreground-400">共 {specials.length} 个专题 · 每季度更新</p>
        </div>

        <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-[264px_minmax(0,1fr)]">
          <aside className="lg:sticky lg:top-[88px] lg:self-start">
            <div className="rounded-lg border border-background-200/90 bg-background-50">
              <p className="border-b border-background-200 px-4 py-3 text-[12px] font-semibold text-foreground-900">专题导航</p>
              <ul className="p-2">
                {specials.map((item) => {
                  const isActive = item.id === active.id;
                  return (
                    <li key={item.id}>
                      <button
                        type="button"
                        onClick={() => setSearchParams({ id: item.id }, { replace: true })}
                        className={`mb-1 flex w-full items-start gap-2.5 rounded-md px-2.5 py-2.5 text-left transition-colors ${
                          isActive ? "bg-primary-50" : "hover:bg-background-100"
                        }`}
                      >
                        <span
                          className={`mt-0.5 h-9 w-1 shrink-0 rounded-full ${
                            isActive ? "bg-accent-500" : "bg-background-300"
                          }`}
                        />
                        <span className="min-w-0">
                          <span
                            className={`block text-[13px] font-medium ${
                              isActive ? "text-primary-700" : "text-foreground-800"
                            }`}
                          >
                            {item.title}
                          </span>
                          <span className="mt-0.5 block truncate text-[10px] text-foreground-500">{item.subtitle}</span>
                          <span className="mt-1 block text-[10px] text-foreground-400">{item.filmIds.length} 部作品</span>
                        </span>
                      </button>
                    </li>
                  );
                })}
              </ul>
              <div className="border-t border-background-200 p-3">
                <Link
                  to="/category"
                  className="flex items-center justify-center gap-1 whitespace-nowrap rounded-md border border-background-300 py-2 text-[12px] text-foreground-600 transition-colors hover:border-primary-300 hover:text-primary-700"
                >
                  浏览全部分类
                  <i className="ri-arrow-right-s-line" />
                </Link>
              </div>
            </div>
          </aside>

          <div className="min-w-0">
            <section className="overflow-hidden rounded-lg border border-background-200/90 bg-background-50">
              <div className="relative h-[220px] w-full md:h-[300px]">
                <img
                  src={active.cover}
                  alt={`${active.title} 专题封面`}
                  title={`${active.title} 韩国影视专题`}
                  className="h-full w-full object-cover object-center"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-primary-950/90 via-primary-950/35 to-transparent" />
                <div className="absolute inset-x-0 bottom-0 p-5 md:p-6">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="rounded bg-accent-500 px-2 py-1 text-[10px] font-semibold text-primary-950">专题</span>
                    <span className="text-[11px] text-background-200">
                      {active.filmIds.length} 部作品 · {active.subtitle}
                    </span>
                  </div>
                  <h2 className="mt-2.5 font-heading text-[24px] font-semibold text-background-50 md:text-[30px]">
                    {active.title}
                  </h2>
                </div>
              </div>

              <div className="p-5 md:p-6">
                <p className="text-[13px] leading-8 text-foreground-700">{active.intro}</p>
                <div className="mt-4 rounded-md border border-accent-200 bg-accent-50/70 p-4">
                  <h3 className="flex items-center gap-1.5 text-[12px] font-semibold text-accent-900">
                    <i className="ri-lightbulb-flash-line" />
                    推荐理由
                  </h3>
                  <p className="mt-1.5 text-[12px] leading-7 text-accent-900/85">{active.reason}</p>
                </div>
              </div>
            </section>

            <section className="mt-6">
              <div className="mb-4 flex items-end justify-between">
                <div>
                  <h2 className="font-heading text-[19px] font-semibold text-foreground-950">专题片单</h2>
                  <p className="mt-1 text-[12px] text-foreground-500">按专题顺序排列，点击卡片查看剧情详情。</p>
                </div>
                <span className="text-[11px] text-foreground-400">{activeFilms.length} 部</span>
              </div>

              {activeFilms.length === 0 ? (
                <div className="rounded-lg border border-dashed border-background-300 bg-background-100 px-6 py-16 text-center">
                  <p className="text-sm text-foreground-600">暂无相关专题</p>
                  <p className="mt-1 text-[12px] text-foreground-400">该合集的作品正在整理中</p>
                </div>
              ) : (
                <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:gap-4 lg:grid-cols-4">
                  {activeFilms.map((film) => (
                    <FilmCard key={film.id} film={film} />
                  ))}
                </div>
              )}
            </section>

            <section className="mt-8">
              <h2 className="mb-3 text-[13px] font-semibold text-foreground-900">相关专题</h2>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
                {relatedSpecials.map((item) => (
                  <button
                    key={item.id}
                    type="button"
                    onClick={() => {
                      setSearchParams({ id: item.id }, { replace: true });
                      window.scrollTo({ top: 120, behavior: "smooth" });
                    }}
                    className="group flex items-center gap-3 rounded-lg border border-background-200/90 bg-background-50 p-3 text-left transition-colors hover:border-primary-300"
                  >
                    <span className="h-14 w-20 shrink-0 overflow-hidden rounded bg-background-200">
                      <img
                        src={item.cover}
                        alt={`${item.title} 专题封面`}
                        title={`${item.title} 韩国影视专题`}
                        className="h-full w-full object-cover object-center transition-transform duration-500 group-hover:scale-105"
                      />
                    </span>
                    <span className="min-w-0">
                      <span className="block text-[13px] font-medium text-foreground-900 group-hover:text-primary-700">
                        {item.title}
                      </span>
                      <span className="mt-0.5 block truncate text-[11px] text-foreground-500">{item.subtitle}</span>
                      <span className="mt-1 block text-[10px] text-accent-700">{item.filmIds.length} 部作品</span>
                    </span>
                  </button>
                ))}
              </div>
            </section>
          </div>
        </div>
      </main>

      <SiteFooter />
    </div>
  );
}